import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Formik, FormikProvider } from 'formik';
import { useRecoilValue } from 'recoil';

import {
  MAX_COLUMNS_PER_CATEGORY,
  PORTRAIT_MAX_COLUMNS
} from 'lib/helpers/exportPdf/lib/constants';
import { getAgGridColumns, getAgGridData } from 'lib/helpers/exportPdf/lib/getAgGridData';
import { printTable } from 'lib/helpers/exportPdf/printPdf';
import { ia, iaRa, snakeToTitleCase } from 'lib/helpers/utility';

import {
  generateColumnDefs,
  generateHeaderName
} from 'components/practice/BeyondBilling/Reports/components/ReportView/lib/generateColumnDefs';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';

import exportToCsv from '../../lib/exportToCsv';
import exportToExcel from '../../lib/exportToExcel';
import { flattenNestedData } from '../../lib/flattenNestedData';
import validationSchema from '../../lib/validationSchema';
import Footer from '../Footer/Footer';

import {
  ActiveFilterList,
  ExportColumnsCheckbox,
  ExportInputTitle,
  ExportTypeCheckbox
} from './components';
import ExportDataRadio from './components/ExportDataRadio';
import ExportOrientation from './components/ExportOrientation';

const Content = ({ closePopover, useData = () => ({}), customContent }) => {
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);

  const {
    name,
    title,
    filters = {},
    colDefs,
    getData,
    gridApi,
    statusPanelData,
    printExpandedRows,
    queryParams,
    patient,
    exportColumnsKeys,
    onExportSubmit
  } = useData();

  const columns = useMemo(() => {
    const keys = iaRa(exportColumnsKeys);
    if (keys.length && keys[0] !== 'all') {
      const filtered = colDefs
        .filter((col) => keys.includes(col.field))
        .filter((col) => (col.headerName || '').toLowerCase() !== 'actions');
      if (filtered.length) return filtered;
    }

    let columns = colDefs.filter(
      (item) => item.headerName && item.headerName?.toLowerCase() !== 'actions'
    );

    if (!columns.length && gridApi) {
      columns = getAgGridColumns(gridApi, false);
    }

    return Object.values(
      columns.reduce((acc, col) => {
        acc[col.field] = col;
        return acc;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, colDefs.length, exportColumnsKeys]);

  const hasExpandableRows = !!printExpandedRows;

  const childColumns = useMemo(() => {
    if (!hasExpandableRows || !gridApi) return [];

    let children = [];
    gridApi.forEachNode((node) => {
      if (!children.length && node.data?.children?.length) {
        children = node.data.children;
      }
    });

    if (!ia(children)) return [];
    return generateColumnDefs(children);
  }, [gridApi, hasExpandableRows]);

  const initialValues = {
    title: snakeToTitleCase(title || name),
    export_type: [],
    orientation: columns.length > PORTRAIT_MAX_COLUMNS ? 'landscape' : 'portrait',
    export_columns: ['all'],
    selected_columns: [],
    export_data: 'summary'
  };

  const handleExport = async (values, otherProps) => {
    try {
      otherProps.setSubmitting(true);

      let data = [];
      const isDetailedMode = hasExpandableRows && values.export_data === 'detailed';
      const activeColumns = isDetailedMode ? childColumns : columns;
      let finalColumns = activeColumns;

      const isExportAllColumns = values.export_columns.includes('all');

      if (!isExportAllColumns) {
        const sortedSelectedColumns = values.selected_columns.sort((a, b) => {
          return (
            activeColumns.findIndex((col) => col.field === a) -
            activeColumns.findIndex((col) => col.field === b)
          );
        });

        finalColumns = sortedSelectedColumns.map((col) => {
          return activeColumns.find((column) => column.field === col);
        });
      }

      if (getData) {
        const params = { ...queryParams, filters, exporting: true };
        const response = await getData(navigate, params);

        data = (iaRa(response) ? response : response?.[name]) || [];
      } else if (gridApi) {
        const result = getAgGridData(gridApi, finalColumns, isExportAllColumns);

        data = result.rows;
      }

      if (!data.length) {
        showAlert({ title: 'No data to export!', color: 'warning' });
        return;
      }

      if (typeof onExportSubmit === 'function') {
        await onExportSubmit({
          values,
          data,
          finalColumns,
          isExportAllColumns,
          filters
        });
        closePopover?.();
        return;
      }

      if (values.export_type.includes('csv') || values.export_type.includes('excel')) {
        // Flatten nested children data for CSV/Excel export
        const flattenedData = flattenNestedData(data, finalColumns, {
          mode: hasExpandableRows ? values.export_data : 'summary'
        });

        const columnHeaders = Object.fromEntries(
          finalColumns.map((col) => [col.field, col.headerName])
        );

        const formattedDataColumns = flattenedData.map((obj) => {
          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
              columnHeaders[key] || generateHeaderName(key),
              value
            ])
          );
        });

        if (values.export_type.includes('csv')) {
          exportToCsv(formattedDataColumns, values.title);
        }

        if (values.export_type.includes('excel')) {
          exportToExcel(formattedDataColumns, values.title);
        }
      }

      if (values.export_type.includes('pdf')) {
        printTable({
          category: name,
          tableData: data,
          columns: isDetailedMode ? columns : finalColumns,
          filterHiddenColumns: isDetailedMode || isExportAllColumns,
          statusPanelData: iaRa(statusPanelData).map(({ field, label }) => ({ field, label })),
          printExpandedRows: isDetailedMode,
          orientation: values.orientation,
          maxColumns: MAX_COLUMNS_PER_CATEGORY?.[name],
          headerConfigs: { practice, patient }
        });
      }

      otherProps.setSubmitting(false);
    } catch (error) {
      console.error('Error while exporting table:', error);
    } finally {
      otherProps.setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleExport}
      validationSchema={validationSchema}>
      {(formValues) => {
        return (
          <FormikProvider
            value={{
              ...formValues,
              columns:
                hasExpandableRows && formValues.values.export_data === 'detailed'
                  ? childColumns
                  : columns
            }}>
            <div className="flex flex-col ">
              <div className="flex justify-between gap-4 bg-white px-4 py-2 shadow-[0px_2px_4px_rgba(27,33,35,0.04)]">
                <span className="text-sm font-500 leading-6">Export Report</span>
                <div
                  className="ml-auto flex size-[26px] cursor-pointer select-none items-center justify-center"
                  onClick={closePopover}>
                  <Icon data-qa="close-square-mono" icon="close-linear" onClick={closePopover} />
                </div>
              </div>

              <div className="flex max-h-[400px] flex-col gap-4 overflow-y-auto p-4">
                <ExportInputTitle />
                {customContent}
                <ExportTypeCheckbox />
                <ExportOrientation />
                {hasExpandableRows && <ExportDataRadio />}
                <ExportColumnsCheckbox />
                <ActiveFilterList />
              </div>
            </div>
            <Footer closePopover={closePopover} />
          </FormikProvider>
        );
      }}
    </Formik>
  );
};

export default Content;
